import React, { useEffect, useState } from "react";
import { Siren, AlertTriangle, Play } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, SectionCard, StatusPill } from "@/components/Primitives";
import { ClinicalBanner } from "@/components/Disclaimer";

export default function EmergencyProtocols() {
  const [protocols, setProtocols] = useState([]);
  const [equip, setEquip] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/emergency/protocols").then(r=>setProtocols(r.data));
    api.get("/emergency/equipment").then(r=>setEquip(r.data));
  }, []);

  const activate = (p) => {
    setActive(p);
    toast.success(`Protocol activated: ${p.name}`);
  };

  return (
    <>
      <PageHeader eyebrow="Module 22" title="Emergency Protocol AI"
        subtitle="One-tap protocol activation with step-by-step guidance and equipment readiness." testid="emergency-page" />
      <ClinicalBanner />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <SectionCard title="Protocol Library" testid="protocols">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {protocols.map(p=>(
                <button key={p.name} onClick={()=>activate(p)}
                  className={`text-left p-4 border rounded-lg lift ${p.tier==="critical"?"border-red-200 bg-red-50":"border-brand bg-white"}`}
                  data-testid={`protocol-${p.name.toLowerCase().replace(/\s/g,'-')}`}>
                  <div className="flex items-center justify-between mb-2">
                    <Siren className={`w-5 h-5 ${p.tier==="critical"?"text-red-600":"text-amber-600"}`} />
                    <span className={`status-pill ${p.tier==="critical"?"status-denied":"status-pending"}`}>{p.tier}</span>
                  </div>
                  <div className="text-sm font-semibold">{p.name}</div>
                  <div className="text-[11px] text-brand-muted mt-1">{p.steps.length} steps</div>
                </button>
              ))}
            </div>
          </SectionCard>
        </div>

        <SectionCard title={active?`Active: ${active.name}`:"No active protocol"} testid="active-protocol">
          {active ? (
            <ol className="space-y-2 text-sm">
              {active.steps.map((s,i)=>(
                <li key={i} className="flex gap-2 p-2 rounded hover:bg-brand-bg">
                  <span className="w-6 h-6 rounded-full bg-brand-primary text-white text-xs flex items-center justify-center font-semibold shrink-0">{i+1}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          ) : (
            <div className="text-sm text-brand-muted">Tap a protocol to activate step-by-step guidance.</div>
          )}
        </SectionCard>
      </div>

      <div className="mt-6">
        <SectionCard title="Equipment Readiness" testid="emergency-equip">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {equip.map((e,i)=>(
              <div key={i} className={`p-3 rounded-lg border ${e.alert?"border-red-200 bg-red-50":"border-brand bg-white"}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">{e.name}</div>
                  {e.alert && <AlertTriangle className="w-4 h-4 text-red-600" />}
                </div>
                <div className="text-[11px] text-brand-muted mt-1">Next check: {new Date(e.next_due).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
    </>
  );
}
